import type { FeatureCollection } from 'geojson';
import MapGL, { Source, Layer } from 'react-map-gl';
// eslint-disable-next-line no-duplicate-imports
import type { HeatmapLayer } from 'react-map-gl';
import type { OrderCountryData } from 'tabs/OrderStatistics/types';

const MIN_ZOOM_LEVEL = 2;
const MAX_ZOOM_LEVEL = 4;

// eslint-disable-next-line @typescript-eslint/no-deprecated
export const heatmapLayer: HeatmapLayer = {
  source: 'my-data',
  id: 'heatmap',
  maxzoom: MAX_ZOOM_LEVEL + 1,
  type: 'heatmap',
  paint: {
    'heatmap-weight': ['interpolate', ['linear'], ['get', 'mag'], 0, 0, 100, 1],
    'heatmap-intensity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ZOOM_LEVEL,
      2,
      MAX_ZOOM_LEVEL,
      4,
    ],
    'heatmap-color': [
      'interpolate',
      ['linear'],
      ['heatmap-density'],
      0,
      'rgba(66, 0, 164, 0)',
      0.2,
      'rgba(66, 0, 164, 0.5)',
      0.4,
      '#6400FF',
      1,
      '#9788F7',
    ],
    // Adjust the heatmap radius by zoom level
    'heatmap-radius': [
      'interpolate',
      ['linear'],
      ['zoom'],
      MIN_ZOOM_LEVEL,
      40,
      MAX_ZOOM_LEVEL,
      80,
    ],
    // Transition from heatmap to circle layer by zoom level
    'heatmap-opacity': 1,
  },
};

interface Props {
  countryData: OrderCountryData[];
}

export const Map = ({ countryData }: Props) => {
  const numberOfOrders = countryData.reduce(
    (acc, { orderCount }) => acc + orderCount,
    0
  );

  const rawPercentages = countryData.map(
    ({ orderCount }) => (orderCount / numberOfOrders) * 100
  );

  const maxPercentage = Math.max(...rawPercentages);
  const minPercentage = Math.min(...rawPercentages);

  const countryPercentages = countryData.map(({ orderCount, coordinates }) => {
    const rawPercentage = (orderCount / numberOfOrders) * 100;
    const normalizedPercentage =
      ((rawPercentage - minPercentage) / (maxPercentage - minPercentage)) *
        (100 - 5) +
      5;
    return {
      percentage: normalizedPercentage,
      coordinates,
    };
  });

  const geojson: FeatureCollection = {
    type: 'FeatureCollection',
    features: countryPercentages.map(({ percentage, coordinates }) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates },
      properties: { mag: percentage },
    })),
  };

  return (
    <MapGL
      initialViewState={{
        longitude: 10,
        latitude: 52,
        zoom: MIN_ZOOM_LEVEL + 1,
      }}
      minZoom={MIN_ZOOM_LEVEL}
      maxZoom={MAX_ZOOM_LEVEL}
      mapStyle="mapbox://styles/fredrikomstedt-qb/clujqxm1h002v01pbd6yx4z90"
      attributionControl={false}
    >
      <Source id="my-data" type="geojson" data={geojson}>
        <Layer {...heatmapLayer} />
      </Source>
    </MapGL>
  );
};
